import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FacialMappingMockup } from "@/components/FacialMappingMockup";
import { WhatsAppTestimonials } from "@/components/WhatsAppTestimonials";
import { LiveBuyers, LiveNowBadge } from "@/components/LiveBuyers";
import {
  ScanFace, Scissors, Ban, Wand2, Eye, Shirt, Images, FileText,
  ArrowRight, Check, Lock, Smartphone, FileCheck, Zap, ChevronRight, Clock,
} from "lucide-react";

const MAN = "https://static.prod-images.emergentagent.com/jobs/25bdb5cb-a38e-4afc-ae48-06a4162d0c2e/images/3d734dad3d74353992573cf4ae65b9edf63fb0eb331a0e81ee254e6b66b3b92b.jpeg";
const WOMAN = "https://static.prod-images.emergentagent.com/jobs/25bdb5cb-a38e-4afc-ae48-06a4162d0c2e/images/64df4c648c7812305c69559b5c938c099f11c49d3c79dc25e2e3f96327b8f424.jpeg";

const fade = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const Eyebrow = ({ children }) => (
  <span className="inline-block text-xs md:text-sm tracking-[0.3em] uppercase text-[#C8A46A] font-semibold">{children}</span>
);

export default function Landing() {
  const navigate = useNavigate();
  const [price, setPrice] = useState(67);
  const [exGender, setExGender] = useState("homem");

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config`)
      .then((r) => r.json())
      .then((d) => d?.price_brl && setPrice(d.price_brl))
      .catch(() => {});
  }, []);

  const go = () => navigate("/comecar");
  const P = `R$${Number(price).toFixed(0)}`;

  const CTA = ({ children = "QUERO DESCOBRIR MEU VISUAL", testid, className = "" }) => (
    <button
      onClick={go}
      data-testid={testid}
      className={`group inline-flex items-center justify-center gap-2 rounded-full bg-[#C8A46A] text-[#0F0F10] font-semibold tracking-wide px-8 h-14 text-sm md:text-base hover:bg-[#d9b884] transition-colors duration-300 shadow-[0_12px_40px_-12px_rgba(200,164,106,0.7)] ${className}`}
    >
      {children}
      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0F0F10] text-[#F5F1E8] overflow-x-hidden selection:bg-[#C8A46A] selection:text-[#0F0F10]">
      {/* HEADER */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0F0F10]/85 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="brand-logo">
            <img src="/logo.png" alt="Visagismo IA" className="w-10 h-10 md:w-11 md:h-11 rounded-full object-cover ring-1 ring-[#C8A46A]/40" />
            <span className="font-serif text-lg md:text-xl tracking-wide">Visagismo<span className="text-[#C8A46A]"> IA</span></span>
          </div>
          <button onClick={go} data-testid="nav-cta"
            className="rounded-full border border-[#C8A46A]/50 text-[#C8A46A] hover:bg-[#C8A46A] hover:text-[#0F0F10] transition-colors duration-300 px-4 md:px-6 h-10 md:h-11 text-xs md:text-sm font-semibold tracking-wide">
            QUERO MINHA ANÁLISE
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "radial-gradient(60% 50% at 75% 20%, rgba(200,164,106,0.12), transparent 70%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center pt-14 md:pt-20 pb-16 md:pb-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            <Eyebrow>Visagismo Personalizado com IA</Eyebrow>
            <h1 className="font-serif mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Descubra o visual que <span className="text-[#C8A46A] italic">mais valoriza</span> o seu rosto.
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#F5F1E8]/75 leading-relaxed max-w-xl">
              Antes de cortar, pintar ou mudar seu estilo, descubra quais cortes, barba, sobrancelhas e estilos combinam com as características do seu rosto.
            </p>
            <p className="mt-4 text-sm md:text-base text-[#F5F1E8]/55 leading-relaxed max-w-xl">
              Envie 2 fotos e receba uma análise personalizada de visagismo com referências e orientações para levar ao seu barbeiro ou cabeleireiro.
            </p>
            <div className="mt-9">
              <CTA testid="hero-cta" />
              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                <span className="line-through text-[#F5F1E8]/40">R$450</span>
                <span className="text-[#C8A46A] font-semibold">{P}</span>
                <span className="text-[#F5F1E8]/60">• Pagamento único • Oferta por tempo limitado</span>
              </div>
            </div>
            <div className="mt-6"><LiveNowBadge /></div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#F5F1E8]/70">
              {["Análise personalizada", "Envie pelo celular", "Pagamento seguro"].map((t) => (
                <span key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C8A46A]" /> {t}</span>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.15 }} className="pt-6 lg:pt-0">
            <FacialMappingMockup />
          </motion.div>
        </div>
      </section>

      {/* 3. IDENTIFICAÇÃO COM O PROBLEMA */}
      <Section>
        <motion.div {...fade} className="max-w-3xl">
          <Eyebrow>Você se identifica?</Eyebrow>
          <h2 className="font-serif text-3xl md:text-4xl mt-4 leading-tight">
            Já aconteceu de você mudar o visual e o resultado não ficar como imaginava?
          </h2>
          <div className="mt-6 space-y-4 text-[#F5F1E8]/70 text-base md:text-lg leading-relaxed">
            <p>Você vê um corte bonito em outra pessoa, salva a referência e pensa: <em className="text-[#F5F1E8]">"Será que ficaria bom em mim?"</em></p>
            <p>Nem todo corte combina da mesma forma com todos os rostos.</p>
            <p>Antes de mudar, descubra quais escolhas podem valorizar suas características.</p>
          </div>
          <p className="mt-8 font-serif text-2xl md:text-3xl text-[#C8A46A]">Descubra primeiro. Mude depois.</p>
          <div className="mt-8"><CTA testid="problem-cta" /></div>
        </motion.div>
      </Section>

      {/* 4. O QUE É */}
      <Section alt>
        <motion.div {...fade} className="max-w-3xl mx-auto text-center">
          <Eyebrow>O que é o Visagismo IA</Eyebrow>
          <h2 className="font-serif text-3xl md:text-4xl mt-4 leading-tight">Seu rosto é único. Suas escolhas também podem ser.</h2>
          <p className="mt-6 text-[#F5F1E8]/70 text-base md:text-lg leading-relaxed">
            O Visagismo IA analisa as características do seu rosto e transforma essa análise em recomendações personalizadas para cabelo, barba, sobrancelhas e estilo.
          </p>
          <p className="mt-4 text-[#F5F1E8]/70 text-base md:text-lg leading-relaxed">
            Você recebe um estudo organizado para entender quais escolhas podem valorizar suas características e levar essas referências ao seu profissional.
          </p>
        </motion.div>
      </Section>

      {/* 5. O QUE VOCÊ VAI DESCOBRIR */}
      <Section>
        <motion.div {...fade} className="text-center max-w-2xl mx-auto">
          <Eyebrow>A sua análise</Eyebrow>
          <h2 className="font-serif text-3xl md:text-4xl mt-4">O que você vai descobrir sobre o seu visual</h2>
          <p className="mt-4 text-[#F5F1E8]/60">Uma análise personalizada para entender o que pode valorizar ainda mais as características do seu rosto.</p>
        </motion.div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: ScanFace, t: "Formato do rosto", d: "Identifique o formato e as características que influenciam suas escolhas de visual." },
            { icon: Scissors, t: "Cortes que favorecem", d: "Descubra cortes, comprimentos e estilos que podem harmonizar com seu rosto." },
            { icon: Ban, t: "O que evitar", d: "Entenda quais escolhas podem não favorecer tanto suas proporções." },
            { icon: Wand2, t: "Barba e acabamento", d: "Orientações personalizadas para barba, acabamento e definição." },
            { icon: Eye, t: "Sobrancelhas", d: "Recomendações considerando as características do seu rosto." },
            { icon: Shirt, t: "Estilo", d: "Entenda quais elementos podem contribuir para uma imagem mais harmoniosa." },
            { icon: Images, t: "Referências visuais", d: "Tenha referências para compreender melhor as recomendações." },
            { icon: FileText, t: "Orientações para o profissional", d: "Leve as recomendações diretamente ao seu barbeiro ou cabeleireiro." },
          ].map((c, i) => (
            <motion.div key={c.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.08 }}
              className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 hover:border-[#C8A46A]/40 transition-colors duration-300">
              <div className="w-11 h-11 rounded-full border border-[#C8A46A]/30 flex items-center justify-center mb-4"><c.icon className="w-5 h-5 text-[#C8A46A]" /></div>
              <h3 className="font-serif text-lg">{c.t}</h3>
              <p className="mt-2 text-sm text-[#F5F1E8]/60 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* 6. VEJA O QUE VOCÊ RECEBE */}
      <Section alt>
        <motion.div {...fade} className="text-center max-w-2xl mx-auto">
          <Eyebrow>O material</Eyebrow>
          <h2 className="font-serif text-3xl md:text-4xl mt-4">Veja o que você recebe</h2>
          <p className="mt-4 text-[#F5F1E8]/60">Seu estudo personalizado é organizado em um material profissional, visual e fácil de consultar.</p>
        </motion.div>
        <div className="mt-10 flex justify-center">
          <div className="flex rounded-full border border-[#C8A46A]/30 bg-white/[0.02] p-1">
            {[["homem", "Homem"], ["mulher", "Mulher"]].map(([g, label]) => (
              <button key={g} data-testid={`example-gender-${g}`} onClick={() => setExGender(g)}
                className={`px-6 py-2 rounded-full text-sm font-semibold tracking-wide transition-colors ${exGender === g ? "bg-[#C8A46A] text-[#0F0F10]" : "text-[#F5F1E8]/60 hover:text-[#F5F1E8]"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {[1, 2, 3].map((n) => (
            <motion.div key={`${exGender}-${n}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (n - 1) * 0.08 }}
              className="relative rounded-2xl overflow-hidden border border-[#C8A46A]/25 bg-[#0b0b0d] shadow-2xl">
              <span className="absolute top-3 left-3 z-10 text-[10px] tracking-widest uppercase text-[#0F0F10] bg-[#C8A46A] rounded-full px-3 py-1 font-bold">Página {n}</span>
              <img src={`/exemplo/${exGender}-${n}.png`} alt={`Exemplo do estudo de visagismo — ${exGender} — página ${n}`} loading="lazy" className="w-full h-auto" />
            </motion.div>
          ))}
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            "Análise do formato do rosto",
            "Características faciais",
            "Cortes recomendados",
            "Estilos a evitar",
            "Barba e sobrancelhas",
            "Referências visuais",
            "Orientações para o profissional",
            "Estilo e acabamento",
          ].map((title, i) => (
            <div key={title} className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3">
              <span className="font-serif text-[#C8A46A]/50 text-sm w-6">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-sm text-[#F5F1E8]/80">{title}</span>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-[#F5F1E8]/40">Exemplo real gerado pelo sistema. Cada estudo é único e personalizado conforme as fotos enviadas.</p>
        <div className="mt-10 text-center"><CTA testid="pdf-cta" /></div>
      </Section>

      {/* 7. COMO FUNCIONA */}
      <Section>
        <motion.div {...fade} className="text-center max-w-2xl mx-auto">
          <Eyebrow>Simples e rápido</Eyebrow>
          <h2 className="font-serif text-3xl md:text-4xl mt-4">Como funciona</h2>
        </motion.div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Faça sua análise", d: "Escolha seu perfil e finalize sua compra." },
            { n: "02", t: "Envie 2 fotos", d: "Uma foto de frente e uma foto de perfil, diretamente pelo celular." },
            { n: "03", t: "Receba seu estudo", d: "Receba seu estudo personalizado de visagismo em PDF." },
          ].map((s, i) => (
            <motion.div key={s.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }}
              className="relative rounded-2xl border border-white/8 bg-white/[0.02] p-8">
              <span className="font-serif text-5xl text-[#C8A46A]/30">{s.n}</span>
              <h3 className="font-serif text-xl mt-3">{s.t}</h3>
              <p className="mt-2 text-[#F5F1E8]/60 leading-relaxed">{s.d}</p>
              {i < 2 && <ChevronRight className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-[#C8A46A]/40" />}
            </motion.div>
          ))}
        </div>
        <div className="mt-10 text-center"><CTA testid="how-cta" /></div>
      </Section>

      {/* 8. TRANSFORMAÇÃO / EXEMPLO VISUAL */}
      <Section alt>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fade}>
            <Eyebrow>Personalização</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl mt-4 leading-tight">Seu rosto é único. Seu visual também deve ser.</h2>
            <p className="mt-6 text-[#F5F1E8]/70 text-base md:text-lg leading-relaxed">
              Em vez de escolher um corte apenas porque ficou bonito em outra pessoa, entenda quais características devem ser consideradas no seu caso.
            </p>
            <div className="mt-8"><CTA testid="transform-cta" /></div>
          </motion.div>
          <motion.div {...fade} className="grid grid-cols-2 gap-4">
            {[["Homem", MAN], ["Mulher", WOMAN]].map(([label, src]) => (
              <div key={label} className="relative rounded-2xl overflow-hidden border border-white/8">
                <img src={src} alt={`Exemplo visual ${label}`} className="w-full h-[280px] md:h-[340px] object-cover" />
                <span className="absolute top-3 left-3 text-[10px] tracking-widest uppercase text-[#0F0F10] bg-[#C8A46A] rounded-full px-2.5 py-1 font-bold">Exemplo visual</span>
                <span className="absolute bottom-3 left-3 font-serif text-lg">{label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* DEPOIMENTOS WHATSAPP */}
      <Section>
        <motion.div {...fade} className="text-center max-w-2xl mx-auto">
          <Eyebrow>Quem já descobriu</Eyebrow>
          <h2 className="font-serif text-3xl md:text-4xl mt-4">O que estão dizendo</h2>
          <p className="mt-4 text-[#F5F1E8]/60">Mensagens de quem recebeu o estudo e levou ao seu profissional.</p>
        </motion.div>
        <div className="mt-12"><WhatsAppTestimonials /></div>
        <div className="mt-10 text-center"><CTA testid="testimonials-cta" /></div>
      </Section>

      {/* 9. DESCUBRA ANTES DE MUDAR */}
      <Section>
        <div className="rounded-3xl border border-[#C8A46A]/25 bg-gradient-to-br from-[#17130d] to-[#0F0F10] p-8 md:p-14">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <motion.div {...fade}>
              <Eyebrow>Por que fazer antes</Eyebrow>
              <h2 className="font-serif text-3xl md:text-4xl mt-4">Descubra antes de mudar</h2>
              <p className="mt-6 text-[#F5F1E8]/70 leading-relaxed">Um novo corte, barba ou mudança de estilo pode alterar completamente a percepção do seu rosto.</p>
              <p className="mt-4 text-[#F5F1E8]/70 leading-relaxed">Por {P}, você recebe uma análise personalizada para entender melhor quais escolhas podem valorizar suas características.</p>
              <div className="mt-8"><CTA testid="before-cta" /></div>
            </motion.div>
            <motion.ul {...fade} className="space-y-3">
              {["Entenda o formato do seu rosto", "Descubra cortes que favorecem", "Saiba o que evitar", "Receba orientações de barba", "Receba orientações para sobrancelhas", "Tenha referências visuais", "Leve as recomendações ao seu profissional"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[#F5F1E8]/85"><Check className="w-5 h-5 text-[#C8A46A] shrink-0 mt-0.5" /> {t}</li>
              ))}
            </motion.ul>
          </div>
        </div>
      </Section>

      {/* 11. SEGURANÇA E CONFIANÇA */}
      <Section alt>
        <motion.div {...fade} className="text-center max-w-2xl mx-auto">
          <Eyebrow>Confiança</Eyebrow>
          <h2 className="font-serif text-3xl md:text-4xl mt-4">Uma experiência simples e segura</h2>
        </motion.div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Lock, t: "Pagamento seguro", d: "Pagamento processado pela Kiwify." },
            { icon: Smartphone, t: "Feito pelo celular", d: "Você pode realizar todo o processo diretamente pelo seu celular." },
            { icon: FileCheck, t: "Estudo personalizado", d: "Seu material é preparado de acordo com as fotos enviadas." },
            { icon: Zap, t: "Processo simples", d: "Envie suas fotos e siga as etapas apresentadas." },
          ].map((c) => (
            <motion.div key={c.t} {...fade} className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 text-center">
              <div className="w-11 h-11 rounded-full border border-[#C8A46A]/30 flex items-center justify-center mx-auto mb-4"><c.icon className="w-5 h-5 text-[#C8A46A]" /></div>
              <h3 className="font-serif text-lg">{c.t}</h3>
              <p className="mt-2 text-sm text-[#F5F1E8]/60 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* 10. OFERTA */}
      <Section>
        <motion.div {...fade} className="max-w-2xl mx-auto text-center">
          <Eyebrow>A oferta</Eyebrow>
          <h2 className="font-serif text-3xl md:text-5xl mt-4">Seu estudo personalizado de visagismo</h2>
          <div className="mt-10 rounded-3xl border border-[#C8A46A]/30 bg-gradient-to-b from-[#17130d] to-[#0F0F10] p-8 md:p-12">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#C8A46A]/40 text-[#C8A46A] text-xs font-semibold tracking-wide px-4 py-1.5 mb-6">
              <Clock className="w-3.5 h-3.5" /> OFERTA POR TEMPO LIMITADO
            </span>
            <p className="text-[#F5F1E8]/45 text-xl line-through">De R$450</p>
            <div className="flex items-end justify-center gap-1 mt-1">
              <span className="text-[#F5F1E8]/60 text-xl mb-3">por R$</span>
              <span className="font-serif text-7xl md:text-8xl leading-none text-[#C8A46A]">{Number(price).toFixed(0)}</span>
            </div>
            <p className="text-[#F5F1E8]/60 mt-2">Pagamento único</p>
            <ul className="mt-8 space-y-3 text-left max-w-md mx-auto">
              {["Análise do formato do rosto", "Cortes ideais para suas características", "O que evitar", "Barba e sobrancelhas", "Estilo e acabamento", "Referências visuais", "Orientações para o profissional", "Estudo personalizado em PDF"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[#F5F1E8]/85"><Check className="w-5 h-5 text-[#C8A46A] shrink-0" /> {t}</li>
              ))}
            </ul>
            <div className="mt-9"><CTA testid="offer-cta" className="w-full" /></div>
            <p className="mt-4 text-xs text-[#F5F1E8]/45">Pagamento processado com segurança via Kiwify.</p>
          </div>
        </motion.div>
      </Section>

      {/* 13. FAQ */}
      <Section alt>
        <motion.div {...fade} className="text-center max-w-2xl mx-auto">
          <Eyebrow>Dúvidas</Eyebrow>
          <h2 className="font-serif text-3xl md:text-4xl mt-4">Perguntas frequentes</h2>
        </motion.div>
        <div className="mt-10 max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {[
              { q: "Funciona para homens e mulheres?", a: "Sim. Você escolhe o perfil no início da análise e envia suas duas fotos." },
              { q: "Quais fotos preciso enviar?", a: "Uma foto de frente e uma foto de perfil, preferencialmente com boa iluminação e sem filtros." },
              { q: "O que vou receber?", a: "Um estudo personalizado de visagismo em PDF com análise do rosto, recomendações de cortes, estilos, barba, sobrancelhas, acabamento e referências." },
              { q: "Posso levar o estudo para meu barbeiro ou cabeleireiro?", a: "Sim. O estudo foi estruturado para que você possa utilizar as recomendações como referência durante sua conversa com o profissional." },
              { q: "Quanto tempo demora?", a: "Após o envio das informações necessárias, seu estudo é preparado e disponibilizado conforme o processo definido no sistema." },
              { q: "Minhas fotos ficam seguras?", a: "Utilizamos suas fotos para realizar a análise conforme a finalidade apresentada no serviço. Consulte nossa Política de Privacidade para conhecer os detalhes sobre tratamento e armazenamento de dados." },
            ].map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} data-testid={`faq-${i}`} className="border-white/10">
                <AccordionTrigger className="text-left font-medium text-[#F5F1E8] hover:text-[#C8A46A]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#F5F1E8]/65 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* 14. CTA FINAL */}
      <Section>
        <motion.div {...fade} className="text-center max-w-3xl mx-auto">
          <h2 className="font-serif text-3xl md:text-5xl leading-tight">Sua próxima transformação começa com <span className="text-[#C8A46A] italic">2 fotos</span>.</h2>
          <p className="mt-5 text-[#F5F1E8]/70 text-base md:text-lg">Descubra quais escolhas podem valorizar ainda mais o seu rosto antes de mudar seu visual.</p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <span className="font-serif text-3xl text-[#F5F1E8]/40 line-through">R$450</span>
            <span className="font-serif text-5xl text-[#C8A46A]">{P}</span>
          </div>
          <div className="mt-7"><CTA testid="final-cta" /></div>
          <p className="mt-4 text-xs text-[#F5F1E8]/45">Pagamento único • Oferta por tempo limitado</p>
        </motion.div>
      </Section>

      {/* FOOTER */}
      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-5 text-sm text-[#F5F1E8]/50">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Visagismo IA" className="w-9 h-9 rounded-full object-cover ring-1 ring-[#C8A46A]/40" />
            <span className="font-serif text-lg text-[#F5F1E8]">Visagismo<span className="text-[#C8A46A]"> IA</span></span>
          </div>
          <p className="order-3 md:order-2">© {new Date().getFullYear()} Visagismo IA · Todos os direitos reservados</p>
          <div className="flex items-center gap-5 order-2 md:order-3">
            <a href="/privacidade" target="_blank" rel="noopener noreferrer" data-testid="footer-privacy-link" className="hover:text-[#C8A46A] transition-colors">Política de Privacidade</a>
            <a href="/termos" target="_blank" rel="noopener noreferrer" data-testid="footer-terms-link" className="hover:text-[#C8A46A] transition-colors">Termos de Uso</a>
            <button onClick={() => navigate("/admin")} data-testid="admin-link" className="hover:text-[#C8A46A] transition-colors">Admin</button>
          </div>
        </div>
      </footer>
      <LiveBuyers />
    </div>
  );
}

function Section({ children, alt = false }) {
  return (
    <section className={alt ? "bg-white/[0.015] border-y border-white/5" : ""}>
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">{children}</div>
    </section>
  );
}
